import type { FilterOption } from '@odo/shared-ui';
import type { DmNotificationLevelCounts } from '../../../services/dmNotifications';

export type DmNotificationLevel = 'alert' | 'warning' | 'info' | 'goal-achieved' | 'none';

const KNOWN_LEVELS: readonly DmNotificationLevel[] = [
    'alert',
    'warning',
    'info',
    'goal-achieved',
    'none'
];

export function normalizeLevel(level: string | null | undefined): DmNotificationLevel {
    return KNOWN_LEVELS.find((known) => known === level) ?? 'none';
}

const OTHER_LEVEL_FILTER = 'other';

interface LevelPillStyle {
    label: string;
    // Background + text: SolidStatusPill's tone replaces its hashed colour outright.
    toneClassName: string;
    dotLevel: DmNotificationLevel;
}

const LEVEL_PILL_STYLES: Partial<Record<DmNotificationLevel, LevelPillStyle>> = {
    alert: { label: 'Alert', toneClassName: 'bg-red-50 text-red-700', dotLevel: 'alert' },
    warning: { label: 'Warning', toneClassName: 'bg-amber-50 text-amber-700', dotLevel: 'warning' },
    info: { label: 'Info', toneClassName: 'bg-blue-50 text-blue-700', dotLevel: 'info' }
};

const OTHER_PILL_STYLE: LevelPillStyle = {
    label: 'Other',
    toneClassName: 'bg-gray-100 text-gray-600',
    dotLevel: 'none'
};

export function levelPillStyle(level: DmNotificationLevel): LevelPillStyle {
    return LEVEL_PILL_STYLES[level] ?? OTHER_PILL_STYLE;
}

const LEVEL_FILTER_LABELS: Readonly<Record<string, string>> = {
    alert: 'Alert',
    warning: 'Warning',
    info: 'Info',
    // The API reads "other" as any level but alert, warning and info, a missing one included.
    [OTHER_LEVEL_FILTER]: 'Other'
};

export const LEVEL_FILTER_OPTIONS: readonly FilterOption[] = Object.entries(
    LEVEL_FILTER_LABELS
).map(([value, label]) => ({ value, label }));

const LEVEL_FILTER_VALUES: ReadonlySet<string> = new Set(Object.keys(LEVEL_FILTER_LABELS));

// Values from a stale or hand-edited URL are dropped rather than sent to the API.
export function levelFilterToApi(selected: readonly string[]): string[] {
    return selected.filter((value) => LEVEL_FILTER_VALUES.has(value));
}

// In the filter's own order whatever order they were picked in; unknown values dropped as above.
export function levelFilterLabels(selected: readonly string[]): string[] {
    const chosen = new Set(selected);
    return Object.entries(LEVEL_FILTER_LABELS)
        .filter(([value]) => chosen.has(value))
        .map(([, label]) => label);
}

interface LevelCountEntry {
    key: keyof DmNotificationLevelCounts;
    level: DmNotificationLevel;
}

export const LEVEL_COUNT_ENTRIES: readonly LevelCountEntry[] = [
    { key: 'alert', level: 'alert' },
    { key: 'warning', level: 'warning' },
    { key: 'info', level: 'info' },
    { key: 'other', level: 'none' }
];

// Urgency rank from LEVEL_COUNT_ENTRIES' order (0 = alert); anything else ranks with "other".
export function levelRank(level: string | null | undefined): number {
    const normalized = normalizeLevel(level);
    const index = LEVEL_COUNT_ENTRIES.findIndex((entry) => entry.level === normalized);
    return index === -1 ? LEVEL_COUNT_ENTRIES.length - 1 : index;
}
